import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

export default function AuthModal({ initialMode = 'login', onClose, onSuccess }) {
  const { login, loginWithGoogle } = useAuth();
  const [mode, setMode] = useState(initialMode); // 'login' | 'signup'

  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [loading,  setLoading]  = useState(false);

  const handleGoogle = async () => {
    try {
      const user = await loginWithGoogle();
      if (!user) return; // popup cancelled
      toast.success('Welcome to FindAScholars! 🎉');
      onClose();
      if (onSuccess) onSuccess(user);
    } catch (err) {
      console.error('Google login error:', err);
      toast.error('Could not sign in with Google. Try again.');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Enter email and password'); return; }
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success('Welcome back! 👋');
      onClose();
      if (onSuccess) onSuccess(user);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid email or password');
    }
    setLoading(false);
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.8)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:999,padding:'20px'}}
      onClick={onClose}>
      <div style={{background:'#0a0f1a',borderRadius:'24px',padding:'40px 36px',width:'100%',maxWidth:'420px',border:'1px solid rgba(255,255,255,0.08)',textAlign:'center'}}
        onClick={e => e.stopPropagation()}>

        {/* Logo */}
        <div style={{width:'56px',height:'56px',background:'linear-gradient(135deg,#6366f1,#10b981)',borderRadius:'16px',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 20px'}}>
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
            <path d="M12 3L4 7v5c0 5.25 3.5 10.15 8 11.35C16.5 22.15 20 17.25 20 12V7L12 3z" fill="white" fillOpacity="0.95"/>
            <path d="M9 12l2 2 4-4" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>

        {mode === 'login' ? (
          <>
            <h2 style={{fontFamily:'Space Grotesk,sans-serif',fontSize:'24px',fontWeight:'700',color:'#f1f5f9',marginBottom:'8px'}}>
              Welcome back
            </h2>
            <p style={{fontFamily:'Inter,sans-serif',fontSize:'14px',color:'#818cf8',marginBottom:'24px'}}>
              Login to your FindAScholars account
            </p>

            <form onSubmit={handleLogin} style={{textAlign:'left'}}>
              <label style={{fontFamily:'Inter,sans-serif',fontSize:'12px',color:'#94a3b8',display:'block',marginBottom:'6px'}}>Email Address</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                style={{width:'100%',padding:'12px 14px',background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.12)',borderRadius:'10px',color:'#f1f5f9',fontFamily:'Inter,sans-serif',fontSize:'14px',outline:'none',boxSizing:'border-box',marginBottom:'16px'}}
              />

              <label style={{fontFamily:'Inter,sans-serif',fontSize:'12px',color:'#94a3b8',display:'block',marginBottom:'6px'}}>Password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                style={{width:'100%',padding:'12px 14px',background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.12)',borderRadius:'10px',color:'#f1f5f9',fontFamily:'Inter,sans-serif',fontSize:'14px',outline:'none',boxSizing:'border-box',marginBottom:'20px'}}
              />

              <button type="submit" disabled={loading}
                style={{width:'100%',padding:'13px',background:loading?'#4b4f8f':'#6366f1',border:'none',borderRadius:'12px',color:'#fff',fontFamily:'Inter,sans-serif',fontSize:'15px',fontWeight:'600',cursor:loading?'not-allowed':'pointer'}}>
                {loading ? 'Logging in...' : 'Login →'}
              </button>
            </form>

            <p style={{fontFamily:'Inter,sans-serif',fontSize:'13px',color:'#64748b',marginTop:'20px'}}>
              New here?{' '}
              <span onClick={() => setMode('signup')} style={{color:'#818cf8',cursor:'pointer',fontWeight:'600'}}>
                Create a free account
              </span>
            </p>
          </>
        ) : (
          <>
            <h2 style={{fontFamily:'Space Grotesk,sans-serif',fontSize:'24px',fontWeight:'700',color:'#f1f5f9',marginBottom:'8px'}}>
              Create free account
            </h2>
            <p style={{fontFamily:'Inter,sans-serif',fontSize:'14px',color:'#818cf8',marginBottom:'28px'}}>
              Get 3 free credits — no card needed
            </p>

            <button
              onClick={handleGoogle}
              style={{width:'100%',display:'flex',alignItems:'center',justifyContent:'center',gap:'10px',padding:'14px',background:'#fff',border:'none',borderRadius:'12px',fontFamily:'Inter,sans-serif',fontSize:'15px',fontWeight:'600',color:'#1f2937',cursor:'pointer',transition:'opacity 0.2s'}}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            <p style={{fontFamily:'Inter,sans-serif',fontSize:'12px',color:'#475569',marginTop:'20px',lineHeight:'1.6'}}>
              By continuing, you agree to our Terms of Use and Privacy Policy.
            </p>

            <p style={{fontFamily:'Inter,sans-serif',fontSize:'13px',color:'#64748b',marginTop:'20px'}}>
              Already have an account?{' '}
              <span onClick={() => setMode('login')} style={{color:'#818cf8',cursor:'pointer',fontWeight:'600'}}>
                Login here
              </span>
            </p>
          </>
        )}

        <button onClick={onClose}
          style={{marginTop:'20px',background:'transparent',border:'none',color:'#64748b',fontFamily:'Inter,sans-serif',fontSize:'13px',cursor:'pointer'}}>
          Maybe later
        </button>
      </div>
    </div>
  );
}